import { Dispatch, SetStateAction, useRef, useState } from "react";
import styles from "./CartFormCards.module.css";
import { Action } from "../../../Bundles/Types";
import { CartConfigInterface, initialCartConfig } from "../../CartTypes";
import {
    Button,
    Checkbox,
    Divider,
    FormLayout,
    Icon,
    Select,
    Spinner,
    Text,
    TextField,
    Tooltip,
} from "@shopify/polaris";
import { DeleteIcon, InfoIcon, UploadIcon } from "@shopify/polaris-icons";
import { AvailableCartDrawerTemplates, CustomHtmlPositions } from "../../../Bundles/constants";
import { postData } from "../../../../utilities/networkCalls";
import config from "../../../../config";
import MonacoEditor from "@monaco-editor/react";
import NewCustomColorPicker from "../../../../components/NewCustomColorPicker/NewCustomColorPicker";

export const CartTextsCard = ({
    fields,
    dispatchCartConfig,
    setShowCartEmpty,
}: {
    submit: any;
    fields: any;
    showCartPreview: boolean;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
    setShowCartEmpty: Dispatch<SetStateAction<boolean>>;
}) => {
    const {
        cart_title,
        checkout_cta_text,
        empty_cart_text,
        continue_shopping_cta_text,
        applied_discount_text,
        saving_label_text,
        add_upsell_text,
        estimated_total_text,
        checkout_icon_url,
        upsell_cta_icon_url,
        reward_free_gift_text,
        order_discount_message_text,
        bill_summary_text,
        item_total_text,
        shipping_text,
        shipping_message_text,
        cart_discounts_text,
        grand_total_text,
        free_gift_cta,
        free_gift_free_label,
    } = fields;
    const checkoutInputRef = useRef<HTMLInputElement | null>(null);
    const upsellInputRef = useRef<HTMLInputElement | null>(null);
    const [checkoutIconLoading, setCheckoutIconLoading] = useState(false);
    const [upsellIconLoading, setUpsellIconLoading] = useState(false);
    const [error, setError] = useState("");

    const uploadFile = async (
        event: React.ChangeEvent<HTMLInputElement>,
        setLoading: (val: boolean) => void,
        setError: (msg: string) => void,
        config: { appName: string },
    ): Promise<string | null> => {
        const file = event.target.files?.[0];
        if (!file) return null;

        const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
        if (!allowedTypes.includes(file.type)) {
            setError("Invalid file type. Only JPG and PNG are allowed.");
            return null;
        }

        if (file.size > 1024 * 1024) {
            setError("File too large. Max size is 1MB.");
            return null;
        }

        setLoading(true);
        setError("");

        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await postData({
                url: `/${config.appName}/upload`,
                payload: formData,
            });

            if (!response?.data?.url) {
                throw new Error("Upload failed");
            }

            return response.data.url;
        } catch (err) {
            console.error("Upload failed", err);
            setError("Failed to upload image.");
            return null;
        } finally {
            setLoading(false);
        }
    };

    const updateCheckoutIcon = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const url = await uploadFile(event, setCheckoutIconLoading, setError, config);
        if (!url) return;

        checkout_icon_url.onChange(url);
        dispatchCartConfig({
            payload: url,
            key: "checkout_icon_url",
        });
    };

    const updateUpsellIcon = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const url = await uploadFile(event, setUpsellIconLoading, setError, config);
        if (!url) return;

        upsell_cta_icon_url.onChange(url);
        dispatchCartConfig({
            payload: url,
            key: "upsell_cta_icon_url",
        });
    };

    const triggerFileSelect = (ref: any) => {
        ref.current?.click();
    };

    return (
        <>
            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Text & Labels</div>
                <FormLayout>
                    <div className={`${styles.formRow} ${styles.row1}`}>
                        <div className={styles.settingHeading}>
                            <Text variant="headingMd" as="h5">
                                Cart Interface
                            </Text>
                        </div>

                        <TextField
                            label={"Cart Title"}
                            placeholder={initialCartConfig.cart_title}
                            autoComplete={"off"}
                            {...cart_title}
                            onBlur={cart_title?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "cart_title",
                                });
                                cart_title.onChange(val);
                            }}
                            maxLength={40}
                            showCharacterCount
                        />

                        <div
                            style={{
                                display: "flex",
                                gap: "10px",
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                label={"Checkout Button"}
                                placeholder={initialCartConfig.checkout_cta_text}
                                autoComplete={"off"}
                                {...checkout_cta_text}
                                onBlur={checkout_cta_text?.onBlur}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: val,
                                        key: "checkout_cta_text",
                                    });
                                    checkout_cta_text.onChange(val);
                                }}
                                maxLength={40}
                                showCharacterCount
                                helpText={"Use {icon} to show icon"}
                            />

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                }}
                            >
                                <Tooltip content={checkout_icon_url.value ? "Upload another icon" : "Upload icon"}>
                                    <Button
                                        icon={checkoutIconLoading ? <Spinner size="small" /> : UploadIcon}
                                        onClick={() => triggerFileSelect(checkoutInputRef)}
                                        disabled={checkoutIconLoading}
                                    />
                                </Tooltip>

                                {checkout_icon_url.value && (
                                    <div className={`${styles.badgeIcon} ${styles.selectedBadgeIcon}`}>
                                        <span
                                            className={styles.deleteBadge}
                                            onClick={() => {
                                                dispatchCartConfig({
                                                    payload: "",
                                                    key: "checkout_icon_url",
                                                });
                                                checkout_icon_url.onChange("");
                                            }}
                                        >
                                            <Icon source={DeleteIcon} tone={"critical"} />
                                        </span>
                                        <img
                                            src={checkout_icon_url?.value}
                                            alt={"icon"}
                                            style={{ objectFit: "contain", width: "auto", height: "30px" }}
                                        />
                                    </div>
                                )}

                                {/* Hidden file input */}
                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    ref={checkoutInputRef}
                                    onChange={updateCheckoutIcon}
                                    style={{ display: "none" }}
                                />

                                {error && <p style={{ color: "red", marginLeft: "10px" }}>{error}</p>}
                            </div>
                        </div>

                        <TextField
                            label={"Estimated Total Text"}
                            placeholder={initialCartConfig.estimated_total_text}
                            autoComplete={"off"}
                            {...estimated_total_text}
                            onBlur={estimated_total_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "estimated_total_text",
                                });
                                estimated_total_text.onChange(val);
                            }}
                            maxLength={30}
                            showCharacterCount
                        />

                        <Divider />
                        <div className={styles.settingHeading}>
                            <Text variant="headingMd" as="h5">
                                Empty Cart
                            </Text>
                        </div>

                        <TextField
                            label={"Empty Cart Text"}
                            placeholder={initialCartConfig.empty_cart_text}
                            autoComplete={"off"}
                            {...empty_cart_text}
                            onBlur={() => {
                                empty_cart_text?.onBlur();
                                setShowCartEmpty(false);
                            }}
                            onFocus={() => {
                                setShowCartEmpty(true);
                            }}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "empty_cart_text",
                                });
                                empty_cart_text.onChange(val);
                            }}
                            maxLength={40}
                            showCharacterCount
                        />
                        <TextField
                            label={"Continue Shopping CTA"}
                            placeholder={initialCartConfig.continue_shopping_cta_text}
                            autoComplete={"off"}
                            {...continue_shopping_cta_text}
                            onBlur={() => {
                                continue_shopping_cta_text?.onBlur();
                                setShowCartEmpty(false);
                            }}
                            onFocus={() => {
                                setShowCartEmpty(true);
                            }}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "continue_shopping_cta_text",
                                });
                                continue_shopping_cta_text.onChange(val);
                            }}
                            maxLength={40}
                            showCharacterCount
                        />

                        <Divider />
                        <div className={styles.settingHeading}>
                            <Text variant="headingMd" as="h5">
                                Discounts & Savings
                            </Text>
                        </div>

                        <TextField
                            label={"Applied Discount"}
                            placeholder={initialCartConfig.applied_discount_text}
                            autoComplete={"off"}
                            {...applied_discount_text}
                            onBlur={applied_discount_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "applied_discount_text",
                                });
                                applied_discount_text.onChange(val);
                            }}
                            maxLength={30}
                            showCharacterCount
                        />

                        <TextField
                            label={"Saving Text"}
                            placeholder={initialCartConfig.saving_label_text}
                            autoComplete={"off"}
                            {...saving_label_text}
                            onBlur={saving_label_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "saving_label_text",
                                });
                                saving_label_text.onChange(val);
                            }}
                            maxLength={30}
                            showCharacterCount
                        />

                        <TextField
                            label={"Order Discount Message Text"}
                            placeholder={initialCartConfig.order_discount_message_text}
                            autoComplete={"off"}
                            {...order_discount_message_text}
                            onBlur={order_discount_message_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "order_discount_message_text",
                                });
                                order_discount_message_text.onChange(val);
                            }}
                            maxLength={100}
                            showCharacterCount
                            helpText={
                                <>
                                    <span>{`Use {amount_saved} to show saved amount`}</span>
                                    <br />
                                    <span>{`Use {offer} to show discount title`}</span>
                                </>
                            }
                        />

                        <Divider />
                        <div className={styles.settingHeading}>
                            <Text variant="headingMd" as="h5">
                                Upsells
                            </Text>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: "10px",
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                label={"Add Upsell Text"}
                                placeholder={initialCartConfig.add_upsell_text}
                                autoComplete={"off"}
                                {...add_upsell_text}
                                onBlur={add_upsell_text?.onBlur}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: val,
                                        key: "add_upsell_text",
                                    });
                                    add_upsell_text.onChange(val);
                                }}
                                maxLength={30}
                                showCharacterCount
                                helpText={"Use {icon} to show icon"}
                            />

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                }}
                            >
                                <Tooltip content={upsell_cta_icon_url.value ? "Upload another icon" : "Upload icon"}>
                                    <Button
                                        icon={upsellIconLoading ? <Spinner size="small" /> : UploadIcon}
                                        onClick={() => triggerFileSelect(upsellInputRef)}
                                        disabled={upsellIconLoading}
                                    />
                                </Tooltip>

                                {upsell_cta_icon_url.value && (
                                    <div className={`${styles.badgeIcon} ${styles.selectedBadgeIcon}`}>
                                        <span
                                            className={styles.deleteBadge}
                                            onClick={() => {
                                                dispatchCartConfig({
                                                    payload: "",
                                                    key: "upsell_cta_icon_url",
                                                });
                                                upsell_cta_icon_url.onChange("");
                                            }}
                                        >
                                            <Icon source={DeleteIcon} tone={"critical"} />
                                        </span>
                                        <img
                                            src={upsell_cta_icon_url?.value}
                                            alt={"icon"}
                                            style={{ objectFit: "contain", width: "auto", height: "30px" }}
                                        />
                                    </div>
                                )}

                                {/* Hidden file input */}
                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    ref={upsellInputRef}
                                    onChange={updateUpsellIcon}
                                    style={{ display: "none" }}
                                />

                                {error && <p style={{ color: "red", marginLeft: "10px" }}>{error}</p>}
                            </div>
                        </div>

                        <Divider />
                        <div className={styles.settingHeading}>
                            <Text variant="headingMd" as="h5">
                                Free Gift
                            </Text>
                        </div>

                        <TextField
                            label={
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    Free Gift Product Card
                                    <div>
                                        <Tooltip
                                            width="wide"
                                            content={
                                                "This product card appears when the customer qualifies for a free gift through rewards."
                                            }
                                        >
                                            <Icon source={InfoIcon} tone="subdued" />
                                        </Tooltip>
                                    </div>
                                </div>
                            }
                            placeholder={"Grab your Free Gift!"}
                            autoComplete={"off"}
                            {...reward_free_gift_text}
                            onBlur={reward_free_gift_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "reward_free_gift_text",
                                });
                                reward_free_gift_text.onChange(val);
                            }}
                            maxLength={50}
                            showCharacterCount
                        />

                        <TextField
                            label={"Free Gift CTA"}
                            placeholder={initialCartConfig.free_gift_cta}
                            autoComplete={"off"}
                            {...free_gift_cta}
                            onBlur={free_gift_cta?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "free_gift_cta",
                                });
                                free_gift_cta.onChange(val);
                            }}
                            maxLength={40}
                            showCharacterCount
                        />

                        <TextField
                            label={"Free Gift Free Label"}
                            placeholder={initialCartConfig.free_gift_free_label}
                            autoComplete={"off"}
                            {...free_gift_free_label}
                            onBlur={free_gift_free_label?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "free_gift_free_label",
                                });
                                free_gift_free_label.onChange(val);
                            }}
                            maxLength={40}
                            showCharacterCount
                        />

                        <Divider />
                        <div className={styles.settingHeading}>
                            <Text variant="headingMd" as="h5">
                                Bill Summary
                            </Text>
                        </div>

                        <TextField
                            label={"Bill Summary Title"}
                            placeholder={initialCartConfig.bill_summary_text}
                            autoComplete={"off"}
                            {...bill_summary_text}
                            onBlur={bill_summary_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "bill_summary_text",
                                });
                                bill_summary_text.onChange(val);
                            }}
                            maxLength={30}
                            showCharacterCount
                        />
                        <TextField
                            label={"Item Total Text"}
                            placeholder={initialCartConfig.item_total_text}
                            autoComplete={"off"}
                            {...item_total_text}
                            onBlur={item_total_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "item_total_text",
                                });
                                item_total_text.onChange(val);
                            }}
                            maxLength={30}
                            showCharacterCount
                        />
                        <TextField
                            label={"Shipping Text"}
                            placeholder={initialCartConfig.shipping_text}
                            autoComplete={"off"}
                            {...shipping_text}
                            onBlur={shipping_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "shipping_text",
                                });
                                shipping_text.onChange(val);
                            }}
                            maxLength={30}
                            showCharacterCount
                        />
                        <TextField
                            label={"Shipping Message Text"}
                            placeholder={initialCartConfig.shipping_message_text}
                            autoComplete={"off"}
                            {...shipping_message_text}
                            onBlur={shipping_message_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "shipping_message_text",
                                });
                                shipping_message_text.onChange(val);
                            }}
                            maxLength={50}
                            showCharacterCount
                        />
                        <TextField
                            label={"Cart Discounts Text"}
                            placeholder={initialCartConfig.cart_discounts_text}
                            autoComplete={"off"}
                            {...cart_discounts_text}
                            onBlur={cart_discounts_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "cart_discounts_text",
                                });
                                cart_discounts_text.onChange(val);
                            }}
                            maxLength={30}
                            showCharacterCount
                        />
                        <TextField
                            label={"Grand Total Text"}
                            placeholder={initialCartConfig.grand_total_text}
                            autoComplete={"off"}
                            {...grand_total_text}
                            onBlur={grand_total_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "grand_total_text",
                                });
                                grand_total_text.onChange(val);
                            }}
                            maxLength={30}
                            showCharacterCount
                        />
                    </div>
                </FormLayout>
            </div>
        </>
    );
};

const CartDiscountsCard = ({
    fields,
    dispatchCartConfig,
}: {
    submit: any;
    fields: any;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
}) => {
    const {
        discount_combinations: {
            reward_free_gift_order_discounts,
            reward_free_gift_product_discounts,
            reward_free_gift_shipping_discounts,
            reward_order_order_discounts,
            reward_order_product_discounts,
            reward_order_shipping_discounts,
            upsell_order_discounts,
            upsell_product_discounts,
            upsell_shipping_discounts,
        },
    } = fields;
    return (
        <div className={styles.designCardWrapper}>
            <div className={styles.cardLabel}>Discount Combination</div>
            <FormLayout>
                <div className={styles.cardSubLabel} style={{ marginTop: 5 }}>
                    Choose discounts to combine with Upsell Discounts
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                        paddingTop: 5,
                    }}
                >
                    {upsell_order_discounts && (
                        <Checkbox
                            label={"Order discounts"}
                            {...upsell_order_discounts}
                            value=""
                            checked={upsell_order_discounts.value}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: {
                                        upsell_order_discounts: val,
                                    },
                                    isComplex: true,
                                    key: "discount_combinations",
                                });
                                upsell_order_discounts.onChange(val);
                            }}
                        />
                    )}
                    {upsell_product_discounts && (
                        <Checkbox
                            label={"Product discounts"}
                            {...upsell_product_discounts}
                            value=""
                            checked={upsell_product_discounts.value}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: {
                                        upsell_product_discounts: val,
                                    },
                                    isComplex: true,
                                    key: "discount_combinations",
                                });
                                upsell_product_discounts.onChange(val);
                            }}
                        />
                    )}
                    {upsell_shipping_discounts && (
                        <Checkbox
                            label={"Shipping discounts"}
                            {...upsell_shipping_discounts}
                            value=""
                            checked={upsell_shipping_discounts.value}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: {
                                        upsell_shipping_discounts: val,
                                    },
                                    isComplex: true,
                                    key: "discount_combinations",
                                });
                                upsell_shipping_discounts.onChange(val);
                            }}
                        />
                    )}
                </div>
                <Divider />
                <div className={styles.cardSubLabel}>Choose discounts to combine with Rewards Discounts</div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                        paddingTop: 5,
                    }}
                >
                    {reward_free_gift_order_discounts && (
                        <Checkbox
                            label={"Order discounts"}
                            {...reward_free_gift_order_discounts}
                            value=""
                            checked={reward_free_gift_order_discounts.value}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: {
                                        reward_free_gift_order_discounts: val,
                                    },
                                    isComplex: true,
                                    key: "discount_combinations",
                                });
                                reward_free_gift_order_discounts.onChange(val);
                            }}
                        />
                    )}
                    {reward_free_gift_product_discounts && (
                        <Checkbox
                            label={"Product discounts"}
                            {...reward_free_gift_product_discounts}
                            value=""
                            checked={reward_free_gift_product_discounts.value}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: {
                                        reward_free_gift_product_discounts: val,
                                    },
                                    isComplex: true,
                                    key: "discount_combinations",
                                });
                                reward_free_gift_product_discounts.onChange(val);
                            }}
                        />
                    )}
                    {reward_free_gift_shipping_discounts && (
                        <Checkbox
                            label={"Shipping discounts"}
                            {...reward_free_gift_shipping_discounts}
                            value=""
                            checked={reward_free_gift_shipping_discounts.value}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: {
                                        reward_free_gift_shipping_discounts: val,
                                    },
                                    isComplex: true,
                                    key: "discount_combinations",
                                });
                                reward_free_gift_shipping_discounts.onChange(val);
                            }}
                        />
                    )}
                </div>
            </FormLayout>
        </div>
    );
};

export const AdditionalCard = ({
    fields,
    dispatchCartConfig,
}: {
    submit: any;
    fields: any;
    showCartPreview: boolean;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
    setShowCartEmpty: Dispatch<SetStateAction<boolean>>;
}) => {
    const { show_underline_below_cart_text } = fields;
    return (
        <>
            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Additional</div>
                <FormLayout>
                    <div className={`${styles.formRow} ${styles.row1}`}>
                        <Checkbox
                            label={"Show underline below cart text"}
                            {...show_underline_below_cart_text}
                            value=""
                            checked={show_underline_below_cart_text.value}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: val,
                                    key: "show_underline_below_cart_text",
                                });
                                show_underline_below_cart_text.onChange(val);
                            }}
                        />
                    </div>
                </FormLayout>
            </div>
        </>
    );
};

export const TermsAndConditionsCard = ({
    fields,
    dispatchCartConfig,
}: {
    submit: any;
    fields: any;
    showCartPreview: boolean;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
    setShowCartEmpty: Dispatch<SetStateAction<boolean>>;
}) => {
    const {
        terms_and_conditions: { status, text, required_text },
        terms_and_conditions_design: { text_color, link_color },
    } = fields;

    return (
        <div className={styles.designCardWrapper}>
            <div className={styles.cardLabel}>Terms and Conditions</div>
            <FormLayout>
                <div className={`${styles.formRow} ${styles.row1}`}>
                    <Checkbox
                        label={"Show terms and conditions"}
                        {...status}
                        value=""
                        checked={status.value}
                        onChange={(val) => {
                            dispatchCartConfig({
                                payload: { status: val },
                                isComplex: true,
                                key: "terms_and_conditions",
                            });
                            status.onChange(val);
                        }}
                    />
                </div>
                {status.value && (
                    <div className={`${styles.formRow} ${styles.row1}`}>
                        <TextField
                            label={"T&C text"}
                            placeholder={
                                "I have read and agree to the <a target='_blank' href='https://www.example.com'>terms and conditions</a>"
                            }
                            autoComplete={"off"}
                            {...text}
                            onBlur={text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: { text: val },
                                    isComplex: true,
                                    key: "terms_and_conditions",
                                });
                                text.onChange(val);
                            }}
                        />
                        <TextField
                            label={"T&C required text"}
                            placeholder={"Please agree to the terms and conditions."}
                            autoComplete={"off"}
                            {...required_text}
                            onBlur={required_text?.onBlur}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: { required_text: val },
                                    isComplex: true,
                                    key: "terms_and_conditions",
                                });
                                required_text.onChange(val);
                            }}
                        />
                        <div className={`${styles.formRow}`}>
                            <NewCustomColorPicker
                                hexColor={text_color.value}
                                setHexColor={(val) => {
                                    text_color.onChange(val);
                                    dispatchCartConfig({
                                        payload: { text_color: val },
                                        isComplex: true,
                                        key: "terms_and_conditions_design",
                                    });
                                }}
                                label={"T&C text"}
                                props={{
                                    ...text_color,
                                    onBlur: () => text_color?.runValidation(),
                                }}
                            />
                            <NewCustomColorPicker
                                hexColor={link_color.value}
                                setHexColor={(val) => {
                                    link_color.onChange(val);
                                    dispatchCartConfig({
                                        payload: { link_color: val },
                                        isComplex: true,
                                        key: "terms_and_conditions_design",
                                    });
                                }}
                                label={"T&C link"}
                                props={{
                                    ...link_color,
                                    onBlur: () => link_color?.runValidation(),
                                }}
                            />
                        </div>
                    </div>
                )}
            </FormLayout>
        </div>
    );
};

const CartSettingsCard = ({
    fields,
    submit,
    showCartPreview,
    dispatchCartConfig,
    setShowCartEmpty,
}: {
    submit: any;
    fields: any;
    showCartPreview: boolean;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
    setShowCartEmpty: Dispatch<SetStateAction<boolean>>;
}) => {
    const {
        cart_settings: {
            redirect_to_checkout,
            show_text_below_add_to_cart,
            text_below_add_to_cart,
            show_cart_cta,
            cart_position,
            show_continue_shopping_cta,
            open_cart_on_product_add,
            //product&pricing display
            // show_compare_at,
            sticky_footer,
            show_total_saved,
            // show_discounts_form_compare_at,
            // hide_product_variant_label,
            show_product_properties,
            custom_html_position,
            custom_html_text,
            custom_css_text,
            show_compare_at_price,
            //Texts
        },
        view_cart_cta_text,
        continue_shop_cta_text,
        selected_drawer_template,
    } = fields;

    const handleEditorChange = (val?: string) => {
        custom_html_text.onChange(val || "");
        dispatchCartConfig({
            payload: {
                custom_html_text: val || "",
            },
            isComplex: true,
            key: "cart_settings",
        });
    };

    return (
        <>
            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Cart Behavior</div>
                <FormLayout>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                        }}
                    >
                        <div className={`${styles.formRow}`}>
                            <Select
                                label={"Cart Position"}
                                value={cart_position.value}
                                options={[
                                    { label: "Left", value: "left" },
                                    { label: "Right", value: "right" },
                                ]}
                                onChange={(opt) => {
                                    dispatchCartConfig({
                                        payload: {
                                            cart_position: opt,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    cart_position.onChange(opt);
                                }}
                            />
                        </div>
                        <div className={`${styles.formRow}`}>
                            <Checkbox
                                label={"View Cart Button"}
                                {...show_cart_cta}
                                value=""
                                checked={show_cart_cta.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            show_cart_cta: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    show_cart_cta.onChange(val);
                                }}
                            />
                        </div>

                        {show_cart_cta?.value && (
                            <div className={`${styles.formRow}`} style={{ marginTop: -10 }}>
                                <TextField
                                    label={"View cart button"}
                                    labelHidden
                                    placeholder={initialCartConfig.view_cart_cta_text}
                                    autoComplete={"off"}
                                    {...view_cart_cta_text}
                                    onBlur={view_cart_cta_text?.onBlur}
                                    onChange={(val) => {
                                        dispatchCartConfig({
                                            payload: val,
                                            key: "view_cart_cta_text",
                                        });
                                        view_cart_cta_text.onChange(val);
                                    }}
                                    maxLength={40}
                                    showCharacterCount
                                />
                            </div>
                        )}

                        <div className={`${styles.formRow} ${styles.row1}`}>
                            <Checkbox
                                label={`Show ‘Continue Shopping’ Button`}
                                {...show_continue_shopping_cta}
                                value=""
                                checked={show_continue_shopping_cta.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            show_continue_shopping_cta: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    show_continue_shopping_cta.onChange(val);
                                }}
                            />
                        </div>
                        {show_continue_shopping_cta.value && (
                            <div className={`${styles.formRow}`} style={{ marginTop: -10 }}>
                                <TextField
                                    label={"Continue Shopping"}
                                    labelHidden
                                    placeholder={initialCartConfig.continue_shop_cta_text}
                                    autoComplete={"off"}
                                    {...continue_shop_cta_text}
                                    onBlur={continue_shop_cta_text?.onBlur}
                                    onChange={(val) => {
                                        dispatchCartConfig({
                                            payload: val,
                                            key: "continue_shop_cta_text",
                                        });
                                        continue_shop_cta_text.onChange(val);
                                    }}
                                    maxLength={50}
                                    showCharacterCount
                                />
                            </div>
                        )}

                        <div className={`${styles.formRow} ${styles.row1}`}>
                            <Checkbox
                                label={"Direct to Cart Page when Clicking Checkout"}
                                {...redirect_to_checkout}
                                value=""
                                checked={redirect_to_checkout.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            redirect_to_checkout: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    redirect_to_checkout.onChange(val);
                                }}
                            />

                            <Checkbox
                                label={`Open Cart Drawer Right After Adding the Product`}
                                {...open_cart_on_product_add}
                                value=""
                                checked={open_cart_on_product_add.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            open_cart_on_product_add: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    open_cart_on_product_add.onChange(val);
                                }}
                            />
                        </div>
                        <div>
                            <Checkbox
                                label={`Text below add to cart`}
                                {...show_text_below_add_to_cart}
                                value=""
                                checked={show_text_below_add_to_cart?.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            show_text_below_add_to_cart: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    show_text_below_add_to_cart?.onChange(val);
                                }}
                            />
                            {!!show_text_below_add_to_cart?.value && (
                                <TextField
                                    label={"Cart Title"}
                                    placeholder={initialCartConfig?.cart_settings?.text_below_add_to_cart}
                                    autoComplete={"off"}
                                    labelHidden
                                    {...text_below_add_to_cart}
                                    onBlur={text_below_add_to_cart?.onBlur}
                                    onChange={(val) => {
                                        dispatchCartConfig({
                                            payload: {
                                                text_below_add_to_cart: val,
                                            },
                                            isComplex: true,
                                            key: "cart_settings",
                                        });
                                        text_below_add_to_cart.onChange(val);
                                    }}
                                    maxLength={50}
                                    showCharacterCount
                                />
                            )}
                        </div>
                    </div>
                </FormLayout>
            </div>
            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Product & Pricing Display</div>
                <FormLayout>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                        }}
                    >
                        {/* <div className={`${styles.formRow}`}>
                            <Checkbox
                                label={"Show Compared at Price"}
                                {...show_compare_at}
                                value=""
                                checked={show_compare_at.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            show_compare_at: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    show_compare_at.onChange(val);
                                }}
                            />
                        </div> */}
                        <div className={`${styles.formRow}`}>
                            <Checkbox
                                label={"Show Products Compare-at Prices"}
                                {...show_compare_at_price}
                                value=""
                                checked={show_compare_at_price.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            show_compare_at_price: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    show_compare_at_price.onChange(val);
                                }}
                            />
                        </div>
                        <div className={`${styles.formRow}`}>
                            <Checkbox
                                label={"Sticky Footer"}
                                {...sticky_footer}
                                value=""
                                checked={sticky_footer.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            sticky_footer: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    sticky_footer.onChange(val);
                                }}
                            />
                        </div>

                        <div className={`${styles.formRow} ${styles.row1}`}>
                            <Checkbox
                                label={`Show Savings Below Product Prices`}
                                {...show_total_saved}
                                value=""
                                checked={show_total_saved.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            show_total_saved: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    show_total_saved.onChange(val);
                                }}
                            />
                        </div>
                        {/* <div className={`${styles.formRow} ${styles.row1}`}>
                            <Checkbox
                                label={`Show Discounts from Compare at Price Instead of Base Price`}
                                {...show_discounts_form_compare_at}
                                value=""
                                checked={show_discounts_form_compare_at.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            show_discounts_form_compare_at: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    show_discounts_form_compare_at.onChange(val);
                                }}
                            />
                        </div> */}
                        {/* <div className={`${styles.formRow}`}>
                            <Checkbox
                                label={`Hide Product Variant Label on Cart`}
                                {...hide_product_variant_label}
                                value=""
                                checked={hide_product_variant_label.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            hide_product_variant_label: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    hide_product_variant_label.onChange(val);
                                }}
                            />
                        </div> */}
                        <div className={`${styles.formRow}`}>
                            <Checkbox
                                label={`Show Product Property`}
                                {...show_product_properties}
                                value=""
                                checked={show_product_properties.value}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            show_product_properties: val,
                                        },
                                        isComplex: true,
                                        key: "cart_settings",
                                    });
                                    show_product_properties.onChange(val);
                                }}
                            />
                        </div>
                    </div>
                </FormLayout>
            </div>
            <TermsAndConditionsCard
                submit={submit}
                setShowCartEmpty={setShowCartEmpty}
                dispatchCartConfig={dispatchCartConfig}
                fields={fields}
                showCartPreview={showCartPreview}
            />
            <CartTextsCard
                submit={submit}
                setShowCartEmpty={setShowCartEmpty}
                dispatchCartConfig={dispatchCartConfig}
                fields={fields}
                showCartPreview={showCartPreview}
            />
            <CartDiscountsCard submit={submit} dispatchCartConfig={dispatchCartConfig} fields={fields} />
            {selected_drawer_template.value == AvailableCartDrawerTemplates.MODERN && (
                <AdditionalCard
                    submit={submit}
                    setShowCartEmpty={setShowCartEmpty}
                    dispatchCartConfig={dispatchCartConfig}
                    fields={fields}
                    showCartPreview={showCartPreview}
                />
            )}

            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Custom HTML</div>
                <FormLayout>
                    <div className={`${styles.formRow} ${styles.row1}`}>
                        <Select
                            label={"Custom HTML Position"}
                            value={custom_html_position.value}
                            options={CustomHtmlPositions}
                            onChange={(opt) => {
                                dispatchCartConfig({
                                    payload: {
                                        custom_html_position: opt,
                                    },
                                    isComplex: true,
                                    key: "cart_settings",
                                });
                                custom_html_position.onChange(opt);
                            }}
                        />
                    </div>
                    <MonacoEditor
                        className={styles.monacoEditor}
                        height="40vh"
                        value={custom_html_text?.value}
                        defaultLanguage="html"
                        defaultValue={``}
                        onChange={handleEditorChange}
                    />
                </FormLayout>
            </div>
            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    Custom CSS
                    <Tooltip content="Use CSS property !important if styles are not getting applied">
                        <Icon source={InfoIcon} tone="base" />
                    </Tooltip>
                </div>
                <FormLayout>
                    <MonacoEditor
                        className={styles.monacoEditor}
                        height="40vh"
                        value={custom_css_text?.value}
                        defaultLanguage="css"
                        defaultValue={``}
                        onChange={(val) => {
                            custom_css_text.onChange(val || "");
                            dispatchCartConfig({
                                payload: {
                                    custom_css_text: val || "",
                                },
                                isComplex: true,
                                key: "cart_settings",
                            });
                        }}
                    />
                </FormLayout>
            </div>
        </>
    );
};

export default CartSettingsCard;
